/**
 * Discount Code Theme Integration
 * 
 * Reads the discount code from the hidden Liquid-rendered field and applies
 * it across all checkout paths: forms, anchor links, and Shopify SDK buttons.
 * 
 * Compatible with: Standard themes, AJAX cart themes, dynamic checkout buttons.
 * Requires: Step 1 (metafield definition) and Step 2 (Liquid hidden field) complete.
 */

(function () {
  'use strict';

  // ─── 1. READ & VALIDATE THE DISCOUNT CODE ───────────────────────────────────

  const discountField = document.querySelector('input[data-discount]');

  // Guard: field must exist (Step 2 Liquid snippet must be in template)
  if (!discountField) return;

  // Guard: safely read attribute — avoids .trim() crash on undefined
  // Also rejects 'off' in case Liquid misconfiguration passes it through
  const raw = (discountField.dataset.discount || '').trim();
  const discountCode = (raw === 'off' || raw === '') ? null : raw;


  // ─── 2. APPLY SESSION-LEVEL DISCOUNT (for Shopify SDK / Buy Now buttons) ────
  //
  // Shopify Payment SDK buttons (Shop Pay, Apple Pay, Google Pay) are rendered
  // and controlled entirely by Shopify's own scripts. You CANNOT intercept them
  // via DOM events or href modification. The only reliable way to apply a discount
  // to these buttons is to set it at the session level via /discount/:code endpoint.
  //
  // Credit: ChatGPT analysis — this is the correct approach for SDK buttons.

  if (discountCode) {
    fetch(`/discount/${encodeURIComponent(discountCode)}`, {
      method: 'GET',
      credentials: 'same-origin',
    }).catch(function (err) {
      console.warn('[Discount Integration] Session discount fetch failed:', err);
    });
  }


  // ─── 3. CORE BUTTON UPDATER FUNCTION ────────────────────────────────────────
  //
  // Handles three element types:
  //   <a href="/checkout">     → appends ?discount=CODE to href
  //   <form action="/cart">    → injects or removes a hidden input[name="discount"]
  //   <button name="checkout"> → finds parent form and injects hidden input

  function updateDiscountInButtons() {

    // --- 3a. Checkout anchor links ---
    document.querySelectorAll('a[href*="/checkout"]').forEach(function (el) {
      // Guard: href must exist to avoid getAttribute null crash (ChatGPT fix)
      let href = el.getAttribute('href');
      if (!href) return;

      // Remove any existing discount param first (prevents stacking)
      // Fixed regex: also cleans up orphaned ? or & left behind (Claude + ChatGPT fix)
      href = href
        .replace(/([?&])discount=[^&]*/g, '$1')  // replace param with its separator
        .replace(/[?&]{2,}/g, function (m) { return m[0]; }) // collapse double separators
        .replace(/[?&]$/, '');                    // trim trailing separator

      if (discountCode) {
        const separator = href.includes('?') ? '&' : '?';
        href = `${href}${separator}discount=${encodeURIComponent(discountCode)}`;
      }

      el.setAttribute('href', href);
    });


    // --- 3b. Cart forms (standard Shopify checkout form) ---
    document.querySelectorAll('form[action="/cart"], form[action*="/cart/checkout"]')
      .forEach(function (form) {
        let hidden = form.querySelector('input[name="discount"]');

        if (discountCode) {
          // Create hidden field if it doesn't exist yet
          if (!hidden) {
            hidden = document.createElement('input');
            hidden.type  = 'hidden';
            hidden.name  = 'discount';
            form.appendChild(hidden);
          }
          hidden.value = discountCode;

        } else {
          // Remove field when discount is off — ensures clean state
          if (hidden) hidden.remove();
        }
      });


    // --- 3c. Checkout button (button[name="checkout"]) ---
    //
    // Grok's code selected this element but never handled it as a BUTTON.
    // Fix (Claude analysis): walk up to the parent form and inject there.

    document.querySelectorAll('button[name="checkout"]').forEach(function (btn) {
      const form = btn.closest('form');
      if (!form) return;

      let hidden = form.querySelector('input[name="discount"]');

      if (discountCode) {
        if (!hidden) {
          hidden = document.createElement('input');
          hidden.type  = 'hidden';
          hidden.name  = 'discount';
          form.appendChild(hidden);
        }
        hidden.value = discountCode;

      } else {
        if (hidden) hidden.remove();
      }
    });

  } // end updateDiscountInButtons


  // ─── 4. RUN ON PAGE LOAD ────────────────────────────────────────────────────

  updateDiscountInButtons();


  // ─── 5. WATCH FOR DYNAMICALLY INJECTED BUTTONS ──────────────────────────────
  //
  // Themes with AJAX carts, cart drawers, quick-buy modals, or app-injected
  // sections may add checkout buttons AFTER DOMContentLoaded. MutationObserver
  // re-runs the updater whenever new nodes are added to the DOM.
  //
  // Credit: All three analyses flagged this. MutationObserver is the correct fix.

  const observer = new MutationObserver(function (mutations) {
    // Only re-run if actual nodes were added (avoids thrashing on attr mutations)
    const hadAddedNodes = mutations.some(function (m) {
      return m.addedNodes.length > 0;
    });
    if (hadAddedNodes) updateDiscountInButtons();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });


  // ─── 6. THEME EVENT HOOKS ───────────────────────────────────────────────────
  //
  // Some themes emit custom events when cart state changes (e.g. cart:updated,
  // cart:item-added). Hook into these for maximum compatibility without
  // relying solely on MutationObserver.
  //
  // Add or remove event names to match your specific theme's event API.

  var themeEvents = [
    'cart:updated',
    'cart:item-added',
    'cart:drawer-open',
    'shopify:section:load',   // Shopify theme editor section reload
  ];

  themeEvents.forEach(function (eventName) {
    document.addEventListener(eventName, updateDiscountInButtons);
  });

})(); // end IIFE